import React, { useState, useEffect } from 'react';
import { RecordingSession, ApiResponse } from '../types';
import { apiService, SearchParams } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter from './ScopeFilter';

const SessionsTable: React.FC = () => {
  const [sessions, setSessions] = useState<RecordingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 25,
    search: '',
    sort_by: 'associate_time',
    sort_order: 'DESC'
  });
  // Controlled filter inputs. Component state (rather than uncontrolled
  // inputs) is what keeps the applied values visible: every fetch swaps the
  // whole render tree for the "Loading..." div, which would otherwise reset
  // the inputs to empty.
  const [searchText, setSearchText] = useState('');
  const [startFromText, setStartFromText] = useState('');
  const [startToText, setStartToText] = useState('');
  const [metadataSession, setMetadataSession] = useState<RecordingSession | null>(null);
  const [playbackSession, setPlaybackSession] = useState<RecordingSession | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false
  });

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: ApiResponse<RecordingSession> = await apiService.getSessions(searchParams);
      setSessions(response.sessions || []);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch sessions');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [searchParams]);

  // Parses a 24-hour "YYYY-MM-DD HH:MM[:SS]" filter value (browser local
  // time) into the UTC "YYYY-MM-DD HH:MM:SS" form sessions.php compares
  // against start_time. Text inputs (not datetime-local) guarantee the
  // 24-hour format regardless of the browser/OS locale, which would
  // otherwise render an AM/PM picker.
  const toUtcSqlDatetime = (local: string): string | undefined => {
    const m = local.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return undefined;
    const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 19).replace('T', ' ');
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSearchParams(prev => ({
      ...prev,
      search: searchText.trim(),
      start_from: toUtcSqlDatetime(startFromText),
      start_to: toUtcSqlDatetime(startToText),
      page: 1
    }));
  };

  const handleSort = (field: string) => {
    setSearchParams(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || !isFinite(seconds) || seconds < 0) return 'N/A';
    const whole = Math.floor(seconds);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const secs = whole % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Prefer the recorder-computed duration; fall back to end−start for rows
  // that predate the duration_ms precision fix, and to a live now−start for
  // sessions still recording.
  const sessionDuration = (session: RecordingSession): number | null => {
    if (session.duration > 0) return session.duration;
    if (session.start_time && session.end_time) {
      return (new Date(session.end_time).getTime() - new Date(session.start_time).getTime()) / 1000;
    }
    if (session.start_time && session.status === 'active') {
      return (Date.now() - new Date(session.start_time).getTime()) / 1000;
    }
    return null;
  };

  // rs-metadata's callingParty/calledParty live in netsapiensExtension, a
  // vendor addition — an SRC that sends standards-only metadata leaves
  // those columns empty. Fall back to the SIP From/To, then to the
  // participant list, so a session is never displayed as just "—".
  // (The recorder now falls back to the rs-metadata <participant> entries
  // for new calls; this keeps older rows readable too.)
  const partyDisplay = (session: any, side: 'caller' | 'callee') => {
    const name = side === 'caller' ? session.calling_party_name : session.called_party_name;
    const aor = side === 'caller' ? session.calling_party_aor : session.called_party_aor;
    // The uid identifies the PERSON; the AOR is the device they used on this
    // call. One user can appear under several AORs, so both are shown — the
    // uid is what ties a call to a user, the AOR says which handset.
    // Off-net parties (PSTN) have an AOR and no uid.
    const uid = side === 'caller' ? session.calling_party_uid : session.called_party_uid;
    if (name || aor) {
      return { name: name || '—', aor: aor || '—', uid: uid || null, fallback: false };
    }
    const sipName = side === 'caller' ? session.from_name : session.to_name;
    const sipAddr = side === 'caller'
      ? (session.from_number || session.from_uri)
      : (session.to_number || session.to_uri);
    const participant = Array.isArray(session.participants)
      ? session.participants.find((p: any) => p.role === side)
      : undefined;
    return {
      name: sipName || participant?.name || '—',
      aor: sipAddr || participant?.aor || '—',
      uid: null,
      fallback: true,
    };
  };

  const formatBytes = (bytes: number) => {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, exp)).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
  };

  // Re-indents the stored rs-metadata XML (NetSapiens emits it with one
  // element per line but no indentation). Falls back to the raw string if
  // the document doesn't parse.
  const formatXml = (xml: string): string => {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) {
      return xml;
    }
    const lines: string[] = [];
    const walk = (node: Node, depth: number) => {
      const pad = '  '.repeat(depth);
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.nodeValue || '').trim();
        if (text) lines.push(pad + text);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as Element;
      const attrs = Array.from(el.attributes).map(a => ` ${a.name}="${a.value}"`).join('');
      const children = Array.from(el.childNodes).filter(
        c => c.nodeType === Node.ELEMENT_NODE || (c.nodeType === Node.TEXT_NODE && (c.nodeValue || '').trim())
      );
      if (children.length === 0) {
        lines.push(`${pad}<${el.tagName}${attrs}/>`);
      } else if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
        lines.push(`${pad}<${el.tagName}${attrs}>${(children[0].nodeValue || '').trim()}</${el.tagName}>`);
      } else {
        lines.push(`${pad}<${el.tagName}${attrs}>`);
        children.forEach(c => walk(c, depth + 1));
        lines.push(`${pad}</${el.tagName}>`);
      }
    };
    if (doc.documentElement) {
      walk(doc.documentElement, 0);
      return lines.join('\n');
    }
    return xml;
  };

  if (loading) return <div className="loading">Loading sessions...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="sessions-container">
      <h2>Recording Sessions</h2>

      <ScopeFilter
        value={{ territory: searchParams.territory, domain: searchParams.domain }}
        onChange={s => setSearchParams(prev => ({
          ...prev, territory: s.territory, domain: s.domain, page: 1,
        }))}
      />
      
      <form onSubmit={handleSearch} className="search-form">
        <input
          name="search"
          type="text"
          placeholder="Search by Group ID, calling or called party (partial match)..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <label className="search-time-label">
          From <input
            name="start_from"
            type="text"
            className="monospace"
            placeholder="YYYY-MM-DD HH:MM"
            title="24-hour local time, e.g. 2026-07-13 14:30"
            pattern="\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?"
            size={17}
            value={startFromText}
            onChange={e => setStartFromText(e.target.value)}
          />
        </label>
        <label className="search-time-label">
          To <input
            name="start_to"
            type="text"
            className="monospace"
            placeholder="YYYY-MM-DD HH:MM"
            title="24-hour local time, e.g. 2026-07-13 18:00"
            pattern="\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?"
            size={17}
            value={startToText}
            onChange={e => setStartToText(e.target.value)}
          />
        </label>
        <button type="submit">Search</button>
        <button
          type="button"
          onClick={() => {
            setSearchText('');
            setStartFromText('');
            setStartToText('');
            setSearchParams(prev => ({ ...prev, search: '', start_from: undefined, start_to: undefined, page: 1 }));
          }}
        >
          Clear
        </button>
      </form>

      <div className="pagination-info">
        Showing {sessions.length} of {pagination.total_count} sessions
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('group_id')} className="sortable">
                Group ID {searchParams.sort_by === 'group_id' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('group_seq')} className="sortable">
                Group Seq {searchParams.sort_by === 'group_seq' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('associate_time')} className="sortable">
                Associate Time {searchParams.sort_by === 'associate_time' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('calling_party_name')} className="sortable">
                callingParty {searchParams.sort_by === 'calling_party_name' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('called_party_name')} className="sortable">
                calledParty {searchParams.sort_by === 'called_party_name' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>bargeInParty</th>
              <th onClick={() => handleSort('by_action')} className="sortable">
                byAction {searchParams.sort_by === 'by_action' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('invite_source_ip')} className="sortable">
                Invite Source {searchParams.sort_by === 'invite_source_ip' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Media Sources</th>
              <th>Local RTP Ports</th>
              <th onClick={() => handleSort('duration')} className="sortable">
                Duration {searchParams.sort_by === 'duration' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Bytes</th>
              <th onClick={() => handleSort('end_time')} className="sortable">
                End Time {searchParams.sort_by === 'end_time' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {searchParams.sort_by === 'status' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 ? (
              <tr>
                <td colSpan={16} className="no-data">No sessions found</td>
              </tr>
            ) : (
              sessions.map(session => {
                const localPorts = Array.isArray(session.participants)
                  ? session.participants.map((p: any) => p.rtp_port).filter((port: any) => port !== null && port !== undefined)
                  : [];
                return (
                  <tr key={session.id}>
                    <td className="monospace small">{session.group_id || '—'}</td>
                    <td>{session.group_seq ?? '—'}</td>
                    <td>{formatDateTime(session.associate_time)}</td>
                    {(['caller', 'callee'] as const).map(side => {
                      const p = partyDisplay(session, side);
                      return (
                        <td key={side} className="small"
                          title={p.fallback ? 'From the SIP headers / participant list — this call\'s rs-metadata carried no callingParty/calledParty' : undefined}>
                          {p.name}{p.fallback && p.name !== '—' ? ' *' : ''}{' '}
                          <span className="monospace small">&lt;{p.aor}&gt;</span>
                          {p.uid && (
                            <div className="monospace small party-uid" title="NetSapiens user ID — identifies the person across all their devices">
                              uid: {p.uid}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="small">
                      {(() => {
                        // Barge-in / monitor parties: any participant that is
                        // neither the caller nor the callee (e.g. a user who
                        // barged into the call — role "monitor"). There can be
                        // more than one; the recorder records each on its own
                        // channel.
                        const bargeIn = Array.isArray(session.participants)
                          ? session.participants.filter((p: any) => p.role && p.role !== 'caller' && p.role !== 'callee')
                          : [];
                        if (bargeIn.length === 0) return '—';
                        return bargeIn.map((p: any, i: number) => (
                          <div key={i}>
                            {p.role ? `${p.role}: ` : ''}{p.name || '—'}{' '}
                            <span className="monospace small">&lt;{p.aor || p.number || '—'}&gt;</span>
                            {p.uid && (
                              <div className="monospace small party-uid" title="NetSapiens user ID — identifies the person across all their devices">
                                uid: {p.uid}
                              </div>
                            )}
                          </div>
                        ));
                      })()}
                    </td>
                    <td>
                      {session.by_action || '—'}
                      {session.xferred_group_id && (
                        <div className="monospace small" title="xferredGroupID">↩ {session.xferred_group_id}</div>
                      )}
                    </td>
                    <td className="monospace small">{session.invite_source_ip ? `${session.invite_source_ip}:${session.invite_source_port ?? '—'}` : '—'}</td>
                    <td className="monospace small">
                      {Array.isArray(session.media_sources) && session.media_sources.length > 0
                        ? session.media_sources.map((m: any, i: number) => (
                            <div key={i} title={`${m.participant_role} → local port ${m.rtp_port ?? '—'}`}>
                              {m.participant_ip}:{m.participant_source_port ?? '—'}
                            </div>
                          ))
                        : '—'}
                    </td>
                    <td className="monospace small">{localPorts.length > 0 ? localPorts.join(', ') : '—'}</td>
                    <td>{formatDuration(sessionDuration(session))}</td>
                    <td>{formatBytes(session.total_bytes_recorded || 0)}</td>
                    <td>{formatDate(session.end_time)}</td>
                    <td>
                      <span className={`status status-${session.status}`}>
                        {session.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        type="button"
                        disabled={!session.rs_metadata_xml}
                        title={session.rs_metadata_xml ? 'Show SIPREC rs-metadata' : 'No rs-metadata for this session'}
                        onClick={() => setMetadataSession(session)}
                      >
                        Metadata
                      </button>
                      <button
                        type="button"
                        disabled={!session.wav_filename}
                        title={session.wav_filename ? 'Play recording' : 'No recording file for this session'}
                        onClick={() => setPlaybackSession(session)}
                      >
                        Play
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            disabled={!pagination.has_prev}
            onClick={() => handlePageChange(pagination.current_page - 1)}
          >
            Previous
          </button>

          <span className="page-info">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>

          <button
            disabled={!pagination.has_next}
            onClick={() => handlePageChange(pagination.current_page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {metadataSession && (
        <div className="modal-overlay" onClick={() => setMetadataSession(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SIPREC rs-metadata</h3>
              <button type="button" onClick={() => setMetadataSession(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{metadataSession.call_id}</div>
            <pre className="modal-xml">{formatXml(metadataSession.rs_metadata_xml || '')}</pre>
          </div>
        </div>
      )}

      {playbackSession && (
        <div className="modal-overlay" onClick={() => setPlaybackSession(null)}>
          <div className="modal modal-narrow" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Recording playback</h3>
              <button type="button" onClick={() => setPlaybackSession(null)}>Close</button>
            </div>
            <div className="modal-subtitle monospace small">{playbackSession.call_id}</div>
            <div className="modal-subtitle small">
              {playbackSession.calling_party_name || '—'} → {playbackSession.called_party_name || '—'}
              {' · '}{formatDate(playbackSession.start_time)}
            </div>
            <audio
              controls
              autoPlay
              style={{ width: '100%', marginTop: '0.75rem' }}
              src={`/api/recording_audio.php?id=${playbackSession.id}`}
            >
              Your browser does not support audio playback.
            </audio>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsTable;